import { CartLinesUpdateEvent, CartErrorEvent } from '@shopify/events';
import { formatMoney } from '@theme/money-formatting';

const ERROR_DISPLAY_DURATION = 10000;

/**
 * @typedef {object} BundleVariant
 * @property {number} id
 * @property {string} title
 * @property {number} price - In minor units
 * @property {number} compare - Compare-at price in minor units (0 when none)
 * @property {boolean} available
 * @property {string} image
 */

/**
 * Bundle d'offres (Lumina) : choix d'une offre, d'une variante par article,
 * calcul du prix affiché et ajout au panier de toutes les variantes choisies.
 *
 * Prend la main sur le bouton « Ajouter au panier » du produit (et donc sur la barre
 * d'ajout fixe, qui clique ce bouton) : le formulaire produit n'envoie plus sa variante
 * seule, le bundle envoie les articles de l'offre sélectionnée.
 */
class LuminaBundle extends HTMLElement {
  /** @type {{moneyFormat: string, currency: string, soldOutText: string, errorText: string, variants: BundleVariant[]}} */
  #data = { moneyFormat: '{{amount}}', currency: 'EUR', soldOutText: '', errorText: '', variants: [] };

  /** @type {Map<string, BundleVariant>} */
  #variants = new Map();

  /** @type {HTMLElement | null} */
  #openDropdown = null;

  #busy = false;

  /** @type {number | undefined} */
  #errorTimeout;

  #abortController = new AbortController();

  connectedCallback() {
    const dataElement = this.querySelector('script[data-vb-data]');
    try {
      this.#data = { ...this.#data, ...JSON.parse(dataElement?.textContent || '{}') };
    } catch (error) {
      console.warn('[lumina-bundle] Invalid bundle data', error);
    }
    this.#variants = new Map(this.#data.variants.map((variant) => [String(variant.id), variant]));

    const { signal } = this.#abortController;
    this.addEventListener('click', this.#onClick, { signal });
    this.addEventListener('change', this.#onChange, { signal });
    this.addEventListener('keydown', this.#onKeydown, { signal });
    document.addEventListener('click', this.#onDocumentClick, { signal });
    document.addEventListener('shopify:block:select', this.#onEditorBlockSelect, { signal });
    // Window + capture: runs before the theme's own submit handler, which listens on document.
    window.addEventListener('submit', this.#onSubmit, { capture: true, signal });

    const offers = this.#offers();
    const initialOffer = offers.find((offer) => offer.dataset.preselected === 'true') ?? offers[0];
    if (initialOffer) this.#activate(initialOffer);
    offers.forEach((offer) => this.#updatePrice(offer));
  }

  disconnectedCallback() {
    this.#abortController.abort();
    clearTimeout(this.#errorTimeout);
  }

  /** @returns {HTMLElement[]} */
  #offers() {
    return [...this.querySelectorAll('[data-vb-offer]')].filter((offer) => offer instanceof HTMLElement);
  }

  /** @returns {HTMLElement | null} */
  get activeOffer() {
    return this.querySelector('[data-vb-offer].is-active');
  }

  /**
   * @param {HTMLElement} offer
   * @param {boolean} [syncMedia] - Show the offer's first choice in the product media
   */
  #activate(offer, syncMedia = false) {
    const wasActive = offer.classList.contains('is-active');

    for (const item of this.#offers()) {
      const isActive = item === offer;
      item.classList.toggle('is-active', isActive);
      const radio = item.querySelector('.vb-radio');
      if (radio instanceof HTMLInputElement) radio.checked = isActive;
    }
    this.#closeDropdown();

    if (syncMedia && !wasActive) {
      const [firstVariant] = this.#slotVariants(offer);
      if (firstVariant) this.#syncProductVariant(firstVariant);
    }
  }

  /**
   * Selects the choice in the theme's (hidden) variant picker, so the media gallery, URL and
   * sticky bar switch to it exactly like a click on the picker.
   * @param {BundleVariant} variant
   */
  #syncProductVariant(variant) {
    if (!this.hasAttribute('data-sync-media') || !variant.available) return;

    const section = this.closest('.shopify-section');
    const picker = section?.querySelector(`variant-picker[data-product-id="${this.dataset.productId}"]`);
    if (!picker) return;

    const input = picker.querySelector(`input[type="radio"][data-variant-id="${variant.id}"]`);
    if (input instanceof HTMLInputElement) {
      if (input.checked) return;
      input.checked = true;
      input.dispatchEvent(new Event('change', { bubbles: true }));
      return;
    }

    const option = picker.querySelector(`option[data-variant-id="${variant.id}"]`);
    const select = option?.closest('select');
    if (option instanceof HTMLOptionElement && select && select.value !== option.value) {
      select.value = option.value;
      select.dispatchEvent(new Event('change', { bubbles: true }));
    }
  }

  /** @param {MouseEvent} event */
  #onClick = (event) => {
    const target = event.target;
    if (!(target instanceof Element)) return;

    const option = target.closest('.vb-dropdown__option');
    if (option instanceof HTMLButtonElement) {
      if (!option.disabled) this.#selectOption(option);
      return;
    }

    const button = target.closest('.vb-dropdown__button');
    if (button instanceof HTMLButtonElement) {
      this.#toggleDropdown(button, event.detail === 0);
      return;
    }

    if (target.closest('.vb-dropdown__menu')) return;

    const offer = target.closest('[data-vb-offer]');
    if (offer instanceof HTMLElement && !offer.classList.contains('is-active')) this.#activate(offer, true);
  };

  /** @param {Event} event */
  #onChange = (event) => {
    const radio = event.target;
    if (!(radio instanceof HTMLInputElement) || !radio.classList.contains('vb-radio') || !radio.checked) return;

    const offer = radio.closest('[data-vb-offer]');
    if (offer instanceof HTMLElement) this.#activate(offer, true);
  };

  /**
   * @param {HTMLButtonElement} button
   * @param {boolean} fromKeyboard
   */
  #toggleDropdown(button, fromKeyboard) {
    const dropdown = button.closest('.vb-dropdown');
    if (!(dropdown instanceof HTMLElement)) return;

    if (this.#openDropdown === dropdown) {
      this.#closeDropdown();
      return;
    }

    this.#closeDropdown();
    const menu = dropdown.querySelector('.vb-dropdown__menu');
    if (!(menu instanceof HTMLElement)) return;

    menu.hidden = false;
    button.setAttribute('aria-expanded', 'true');
    this.#openDropdown = dropdown;

    if (fromKeyboard) {
      const selected = menu.querySelector('[aria-selected="true"]:not(:disabled), .vb-dropdown__option:not(:disabled)');
      if (selected instanceof HTMLElement) selected.focus();
    }
  }

  #closeDropdown(returnFocus = false) {
    const dropdown = this.#openDropdown;
    if (!dropdown) return;

    const menu = dropdown.querySelector('.vb-dropdown__menu');
    if (menu instanceof HTMLElement) menu.hidden = true;

    const button = dropdown.querySelector('.vb-dropdown__button');
    button?.setAttribute('aria-expanded', 'false');
    this.#openDropdown = null;

    if (returnFocus && button instanceof HTMLElement) button.focus();
  }

  /** @param {MouseEvent} event */
  #onDocumentClick = (event) => {
    if (this.#openDropdown && event.target instanceof Node && !this.#openDropdown.contains(event.target)) {
      this.#closeDropdown();
    }
  };

  /** @param {KeyboardEvent} event */
  #onKeydown = (event) => {
    if (!this.#openDropdown) return;

    const options = [...this.#openDropdown.querySelectorAll('.vb-dropdown__option:not(:disabled)')];
    const index = options.indexOf(/** @type {Element} */ (document.activeElement));

    switch (event.key) {
      case 'Escape':
        event.preventDefault();
        this.#closeDropdown(true);
        break;
      case 'ArrowDown':
        event.preventDefault();
        /** @type {HTMLElement | undefined} */ (options[Math.min(index + 1, options.length - 1)])?.focus();
        break;
      case 'ArrowUp':
        event.preventDefault();
        /** @type {HTMLElement | undefined} */ (options[Math.max(index - 1, 0)])?.focus();
        break;
      case 'Tab':
        this.#closeDropdown();
        break;
    }
  };

  /** @param {HTMLButtonElement} option */
  #selectOption(option) {
    const slot = option.closest('[data-vb-slot]');
    const variant = this.#variants.get(option.dataset.variantId ?? '');
    if (!(slot instanceof HTMLElement) || !variant) return;

    slot.dataset.variantId = String(variant.id);

    for (const item of slot.querySelectorAll('.vb-dropdown__option')) {
      item.setAttribute('aria-selected', String(item === option));
    }

    const label = slot.querySelector('.vb-dropdown__label');
    if (label) label.textContent = variant.title;

    const swatch = slot.querySelector('.vb-dropdown__button .vb-swatch');
    swatch?.setAttribute('style', option.dataset.swatch ?? '');

    const image = slot.querySelector('.vb-row__image');
    if (image instanceof HTMLImageElement && variant.image) {
      image.src = variant.image;
      image.alt = variant.title;
    }

    this.#closeDropdown(true);

    const offer = slot.closest('[data-vb-offer]');
    if (offer instanceof HTMLElement) this.#updatePrice(offer);

    this.#syncProductVariant(variant);
  }

  /**
   * @param {HTMLElement} offer
   * @returns {BundleVariant[]}
   */
  #slotVariants(offer) {
    return [...offer.querySelectorAll('[data-vb-slot]')]
      .map((slot) => this.#variants.get(/** @type {HTMLElement} */ (slot).dataset.variantId ?? ''))
      .filter((variant) => variant !== undefined);
  }

  /** @param {HTMLElement} offer */
  #updatePrice(offer) {
    const variants = this.#slotVariants(offer);
    if (variants.length === 0) return;

    const base = variants.reduce((sum, variant) => sum + variant.price, 0);
    const compare = variants.reduce((sum, variant) => sum + Math.max(variant.compare || 0, variant.price), 0);
    const value = parseFloat(offer.dataset.discountValue ?? '0') || 0;

    let final = base;
    switch (offer.dataset.priceType) {
      case 'percent':
        final = Math.round((base * (100 - value)) / 100);
        break;
      case 'amount':
        final = Math.max(0, base - Math.round(value * 100));
        break;
      case 'fixed':
        if (value > 0) final = Math.round(value * 100);
        break;
    }

    const finalElement = offer.querySelector('[data-vb-final]');
    if (finalElement) finalElement.textContent = this.#formatMoney(final);

    const compareElement = offer.querySelector('[data-vb-compare]');
    if (compareElement instanceof HTMLElement) {
      const showCompare = offer.dataset.showCompare === 'true' && compare > final;
      compareElement.hidden = !showCompare;
      compareElement.textContent = showCompare ? this.#formatMoney(compare) : '';
    }
  }

  /** @param {number} cents */
  #formatMoney(cents) {
    try {
      return formatMoney(cents, this.#data.moneyFormat, this.#data.currency);
    } catch {
      return (cents / 100).toFixed(2);
    }
  }

  /** @param {Event} event */
  #onEditorBlockSelect = (event) => {
    const target = event.target;
    if (!(target instanceof Element) || !this.contains(target)) return;

    const offer = target.closest('[data-vb-offer]');
    if (offer instanceof HTMLElement) this.#activate(offer);
  };

  /** @param {SubmitEvent} event */
  #onSubmit = (event) => {
    const form = event.target;
    if (!(form instanceof HTMLFormElement)) return;

    const productForm = form.closest('product-form-component');
    if (!(productForm instanceof HTMLElement) || productForm.dataset.productId !== this.dataset.productId) return;
    if (productForm.closest('.shopify-section') !== this.closest('.shopify-section')) return;

    const offer = this.activeOffer;
    if (!offer) return;

    event.preventDefault();
    event.stopImmediatePropagation();
    this.#addToCart(offer);
  };

  /** @param {HTMLElement} offer */
  async #addToCart(offer) {
    if (this.#busy) return;

    const variants = this.#slotVariants(offer);
    if (variants.length === 0) return;

    if (variants.some((variant) => !variant.available)) {
      this.#showError(this.#data.soldOutText);
      return;
    }

    this.#busy = true;
    this.#hideError();

    const propertyName = this.dataset.cartProperty;
    const properties = propertyName && variants.length > 1 ? { [propertyName]: offer.dataset.title ?? '' } : null;

    /** @type {Map<number, number>} */
    const quantities = new Map();
    for (const variant of variants) quantities.set(variant.id, (quantities.get(variant.id) ?? 0) + 1);

    const items = [...quantities].map(([id, quantity]) => ({ id, quantity, ...(properties ? { properties } : {}) }));
    const itemCount = variants.length;

    const sectionIds = [...document.querySelectorAll('cart-items-component')]
      .map((element) => (element instanceof HTMLElement ? element.dataset.sectionId : undefined))
      .filter(Boolean);

    const deferred = CartLinesUpdateEvent.createPromise();
    this.dispatchEvent(
      new CartLinesUpdateEvent({
        action: 'add',
        context: 'product',
        lines: items.map((item) => ({ merchandiseId: String(item.id), quantity: item.quantity })),
        promise: deferred.promise,
      })
    );

    const detail = { source: 'lumina-bundle', itemCount, productId: this.dataset.productId };

    try {
      const response = await fetch(Theme.routes.cart_add_url, {
        method: 'POST',
        headers: { 'Content-Type': 'application/json', Accept: 'application/json' },
        body: JSON.stringify({ items, sections: sectionIds.join(',') }),
      });
      const result = await response.json();

      if (result.status) {
        this.dispatchEvent(
          new CartErrorEvent({
            error: result.message || 'Add to cart failed',
            code: 'INVALID',
            detail: { description: result.description, errors: result.errors },
          })
        );
        const cart = await this.#fetchCart();
        deferred.resolve({
          cart: CartLinesUpdateEvent.createCartFromAjaxResponse(cart),
          detail: { ...detail, items: cart.items, didError: true },
        });
        this.#showError(result.description || result.message || this.#data.errorText);
        return;
      }

      const cart = await this.#fetchCart();
      deferred.resolve({
        cart: CartLinesUpdateEvent.createCartFromAjaxResponse(cart),
        detail: { ...detail, items: cart.items, sections: result.sections, didError: false },
      });
      window.cc?.publish?.('cart_added');
    } catch (error) {
      console.error(error);
      deferred.reject(error);
      this.dispatchEvent(
        new CartErrorEvent({
          error: error instanceof Error ? error.message : 'Network error during add to cart',
          code: 'SERVICE_UNAVAILABLE',
        })
      );
      this.#showError(this.#data.errorText);
    } finally {
      this.#busy = false;
    }
  }

  async #fetchCart() {
    const cartItems = /** @type {(HTMLElement & {fetchCartData?: () => Promise<any>}) | null} */ (
      document.querySelector('cart-items-component')
    );

    if (cartItems) {
      await customElements.whenDefined('cart-items-component');
      if (cartItems.fetchCartData) return cartItems.fetchCartData();
    }

    const response = await fetch(`${Theme.routes.cart_url}.js`, { headers: { Accept: 'application/json' } });
    return response.json();
  }

  /** @param {string} message */
  #showError(message) {
    const error = this.querySelector('.vb-error');
    if (!(error instanceof HTMLElement) || !message) return;

    error.textContent = message;
    error.hidden = false;
    clearTimeout(this.#errorTimeout);
    this.#errorTimeout = setTimeout(() => this.#hideError(), ERROR_DISPLAY_DURATION);
  }

  #hideError() {
    const error = this.querySelector('.vb-error');
    if (error instanceof HTMLElement) error.hidden = true;
  }
}

if (!customElements.get('lumina-bundle')) {
  customElements.define('lumina-bundle', LuminaBundle);
}
